import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Tester verdicts on LLM callouts, scoped to the analysed wording.
const Ctx = createContext(null);

export const feedbackKey = {
  conflict: (c) => `conflict:${c.source || "rulebook"}:${c.section_id}`,
  similar: (s) => `similar:${s.rule.id}`,
  interaction: (it) => `interaction:${[...(it.rule_names || [])].sort().join("|")}:${it.kind || "note"}`,
};

export const isNegative = (rec) => rec && (rec.verdict === "wrong" || rec.verdict === "dismissed");

export function CalloutFeedbackProvider({ scopeText, ruleType, keys, children }) {
  const [mine, setMine] = useState({});
  const [prior, setPrior] = useState({});
  const keySig = (keys || []).join(",");

  useEffect(() => {
    if (!scopeText) return;
    let alive = true;
    api.feedbackQuery({ scope_text: scopeText, keys: keySig ? keySig.split(",") : [] })
      .then((r) => { if (alive) { setMine(r.mine || {}); setPrior(r.prior || {}); } })
      .catch((e) => console.warn("Could not load callout feedback", e));
    return () => { alive = false; };
  }, [scopeText, keySig]);

  const submit = useCallback(async ({ kind, targetKey, verdict, reason, label, callout }) => {
    const rec = await api.saveFeedback({
      scope_text: scopeText, kind, target_key: targetKey, verdict, reason,
      target_label: label, callout, rule_type: ruleType || null,
    });
    setMine((m) => ({ ...m, [targetKey]: rec }));
  }, [scopeText, ruleType]);

  const remove = useCallback(async (targetKey) => {
    const rec = mine[targetKey];
    if (!rec) return;
    await api.deleteFeedback(rec.id);
    setMine((m) => {
      const next = { ...m };
      delete next[targetKey];
      return next;
    });
  }, [mine]);

  const value = useMemo(() => ({ mine, prior, submit, remove }), [mine, prior, submit, remove]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCalloutFeedback = () => useContext(Ctx);

export function useVerdict(targetKey) {
  const ctx = useContext(Ctx);
  return ctx?.mine?.[targetKey] || null;
}
